import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Container, Table, Form, InputGroup, Button, Row, Col, Pagination, Badge, Modal, Spinner, Alert } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import distributorService from '../../../../services/DistributorService';
import { showToast } from '../../../../utils/toast';

const UPIPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [formData, setFormData] = useState({
    utr: '',
    vpa: '',
    orderId: '',
    amount: '',
    transactionDate: new Date().toISOString().split('T')[0],
    status: 'PENDING'
  });
  const fileInputRef = useRef(null);

  // Fetch Payments from API
  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: rowsPerPage,
        search: searchQuery,
        status: statusFilter,
        date_from: dateFrom,
        date_to: dateTo
      };

      const response = await distributorService.getUPIPayments(params);
      
      if (response.success) {
        setPayments(response.data.results || []);
        setTotalCount(response.data.count || 0);
        setSummary(response.data.summary || null);
      } else {
        showToast.error('Failed to fetch UPI payments');
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      showToast.error('Failed to fetch UPI payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPayments();
    }, searchQuery ? 500 : 0); // Debounce search

    return () => clearTimeout(timer);
  }, [currentPage, rowsPerPage, searchQuery, statusFilter, dateFrom, dateTo]);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'status') setStatusFilter(value);
    if (filterType === 'dateFrom') setDateFrom(value);
    if (filterType === 'dateTo') setDateTo(value);
    setCurrentPage(1);
  };

  const exportToExcel = async () => {
    try {
      const response = await distributorService.exportUPIPayments({
        date_from: dateFrom,
        date_to: dateTo,
        status: statusFilter,
        search: searchQuery
      });
      
      if (response.success) {
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.fileName || `upi_payments_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showToast.success('UPI payments exported successfully');
      } else {
        showToast.error('Failed to export UPI payments');
      }
    } catch (error) {
      console.error('Error exporting payments:', error);
      showToast.error('Failed to export UPI payments');
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    try {
      const response = await distributorService.importUPIPayments(file);
      
      if (response.success) {
        showToast.success(response.message || 'UPI payments imported successfully');
        fetchPayments();
      } else {
        showToast.error(response.error || 'Failed to import UPI payments');
      }
    } catch (error) {
      console.error('Error importing payments:', error);
      showToast.error('Error importing UPI payments');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCreatePayment = () => {
    setFormData({
      utr: '',
      vpa: '',
      orderId: '',
      amount: '',
      transactionDate: new Date().toISOString().split('T')[0],
      status: 'PENDING'
    });
    setShowCreateModal(true);
  };

  const handleEditPayment = (payment) => {
    setSelectedPayment(payment);
    setFormData({
      utr: payment.utr,
      vpa: payment.vpa,
      orderId: payment.order_id,
      amount: payment.amount,
      transactionDate: payment.transaction_date ? payment.transaction_date.split('T')[0] : '',
      status: payment.status || 'PENDING'
    });
    setShowEditModal(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // For edit mode, only validate status
    if (selectedPayment) {
      if (!formData.status) {
        showToast.error('Please select a status');
        return;
      }
    } else {
      // For create mode, validate all fields
      if (!formData.utr || !formData.vpa || !formData.orderId || !formData.amount) {
        showToast.error('Please fill in all required fields');
        return;
      }
    }

    setLoading(true);
    try {
      const response = selectedPayment 
        ? await distributorService.updateUPIPayment(selectedPayment.id, { status: formData.status })
        : await distributorService.createUPIPayment(formData);
      
      if (response.success) {
        showToast.success(response.message || 'UPI payment saved successfully');
        setShowCreateModal(false);
        setShowEditModal(false);
        setSelectedPayment(null);
        fetchPayments();
      } else {
        showToast.error(response.error || 'Failed to save UPI payment');
      }
    } catch (error) {
      console.error('Error saving payment:', error);
      showToast.error('Failed to save UPI payment');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this UPI payment?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await distributorService.deleteUPIPayment(paymentId);
      
      if (response.success) {
        showToast.success('UPI payment deleted successfully');
        fetchPayments();
      } else {
        showToast.error('Failed to delete UPI payment');
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
      showToast.error('Failed to delete UPI payment');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'SUCCESS':
        return <Badge bg="success">SUCCESS</Badge>;
      case 'PENDING':
        return <Badge bg="warning">PENDING</Badge>;
      case 'NOTFOUND':
        return <Badge bg="danger">NOTFOUND</Badge>;
      default:
        return <Badge bg="secondary">UNKNOWN</Badge>;
    }
  };

  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-money-check-alt me-2"></i>
          UPI Payments
        </h5>
        <div className="d-flex gap-2">
          <Button variant="success" onClick={exportToExcel} disabled={loading}>
            <i className="fas fa-file-excel me-2"></i>
            Export Excel
          </Button>
          <Button 
            variant="info" 
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
          >
            <i className="fas fa-file-upload me-2"></i>
            {importing ? 'Importing...' : 'Import Excel'}
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImport}
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <Row className="mb-4">
          <Col md={3}>
            <div className="bg-light border border-info rounded p-3 text-center">
              <h5 className="text-info mb-1">₹{summary.total_amount?.toLocaleString()}</h5>
              <small className="text-muted">Total Amount</small>
            </div>
          </Col>
          <Col md={3}>
            <div className="bg-light border border-success rounded p-3 text-center">
              <h5 className="text-success mb-1">{summary.reconciled_count}</h5>
              <small className="text-muted">Reconciled</small>
            </div>
          </Col>
          <Col md={3}>
            <div className="bg-light border border-warning rounded p-3 text-center">
              <h5 className="text-warning mb-1">{summary.unreconciled_count}</h5>
              <small className="text-muted">Unreconciled</small>
            </div>
          </Col>
          <Col md={3}>
            <div className="bg-light border border-primary rounded p-3 text-center">
              <h5 className="text-primary mb-1">{summary.total_count}</h5>
              <small className="text-muted">Total Payments</small>
            </div>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Row className="mb-4">
        <Col md={3}>
          <InputGroup>
            <InputGroup.Text>
              <i className="fas fa-search"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search UTR, VPA, Order ID, Retailer ID..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Status</option>
            <option value="PENDING">PENDING</option>
            <option value="NOTFOUND">NOTFOUND</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Control
            type="date"
            value={dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            placeholder="From Date"
          />
        </Col>
        <Col md={2}>
          <Form.Control
            type="date"
            value={dateTo}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            placeholder="To Date"
          />
        </Col>
        <Col md={3}>
          <div className="d-flex align-items-center">
            <span className="text-muted">
              Showing {payments.length > 0 ? ((currentPage - 1) * rowsPerPage) + 1 : 0} to {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} entries
            </span>
          </div>
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive">
        <Table striped bordered hover className="modern-table">
          <thead>
            <tr>
              <th>Actions</th>
              <th>Status</th>
              <th>UTR</th>
              <th>Amount</th>
              <th>Customer ID</th>
              <th>Shop Name</th>
              <th>VPA / Mobile Number</th>
              <th>Order ID</th>
              <th>Transaction Date</th>
              <th>Distributor</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : payments.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center py-4 text-muted">
                  No UPI payments found
                </td>
              </tr>
            ) : (
              payments.map((payment) => (
                <tr key={payment.id}>
                  <td>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleEditPayment(payment)}
                      title="Edit Payment"
                    >
                      <i className="fas fa-edit"></i>
                    </Button>
                  </td>
                  <td>{getStatusBadge(payment.status)}</td>
                  <td>
                    <div className="text-primary fw-bold">{payment.utr}</div>
                  </td>
                  <td>
                    <div className="text-success fw-bold">₹{parseFloat(payment.amount).toLocaleString()}</div>
                  </td>
                  <td>
                    {payment.retailer_customer_id ? (
                      <span className="text-primary">{payment.retailer_customer_id}</span>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    {payment.retailer_shop_name ? (
                      <strong>{payment.retailer_shop_name}</strong>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>{payment.vpa}</td>
                  <td>
                    <small className="text-muted">{payment.order_id}</small>
                  </td>
                  <td>
                    {new Date(payment.transaction_date).toLocaleDateString('en-IN')}
                    <br />
                    <small className="text-muted">
                      {new Date(payment.transaction_date).toLocaleTimeString('en-IN')}
                    </small>
                  </td>
                  <td>
                    <div>
                      <div><strong>{payment.distributor_name}</strong></div>
                      <small className="text-muted">{payment.distributor_mobile}</small>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          <Form.Select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            style={{ width: 'auto' }}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </Form.Select>
        </div>
        <div>
          Showing {payments.length > 0 ? ((currentPage - 1) * rowsPerPage) + 1 : 0} to {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} entries
        </div>
        <Pagination>
          <Pagination.First 
            onClick={() => setCurrentPage(1)} 
            disabled={currentPage === 1} 
          />
          <Pagination.Prev 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
            disabled={currentPage === 1} 
          />
          {(() => {
            const pages = [];
            const maxVisiblePages = 5;
            
            let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
            let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
            
            if (endPage - startPage < maxVisiblePages - 1) {
              startPage = Math.max(1, endPage - maxVisiblePages + 1);
            }
            
            for (let i = startPage; i <= endPage; i++) {
              pages.push(
                <Pagination.Item
                  key={i}
                  active={i === currentPage}
                  onClick={() => setCurrentPage(i)}
                >
                  {i}
                </Pagination.Item>
              );
            }
            
            return pages;
          })()}
          <Pagination.Next 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
            disabled={currentPage === totalPages} 
          />
          <Pagination.Last 
            onClick={() => setCurrentPage(totalPages)} 
            disabled={currentPage === totalPages} 
          />
        </Pagination>
      </div>

      {/* Create/Edit Modal */}
      <Modal 
        show={showCreateModal || showEditModal} 
        onHide={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          setSelectedPayment(null);
        }} 
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedPayment ? 'Edit UPI Payment' : 'Add New UPI Payment'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleFormSubmit}>
          <Modal.Body>
            {selectedPayment ? (
              /* Edit Mode - Only Status */
              <div>
                <div className="mb-4">
                  <div className="p-4 bg-light rounded border">
                    <h6 className="mb-3 text-primary">
                      <i className="fas fa-info-circle me-2"></i>
                      Payment Details
                    </h6>
                    <Row className="g-3">
                      <Col md={6}>
                        <div className="mb-3">
                          <strong className="text-muted d-block">UTR Number:</strong>
                          <span className="text-dark">{selectedPayment.utr}</span>
                        </div>
                        <div className="mb-3">
                          <strong className="text-muted d-block">VPA:</strong>
                          <span className="text-dark">{selectedPayment.vpa}</span>
                        </div>
                        <div className="mb-3">
                          <strong className="text-muted d-block">Order ID:</strong>
                          <span className="text-dark">{selectedPayment.order_id}</span>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <strong className="text-muted d-block">Amount:</strong>
                          <span className="text-success fw-bold">₹{parseFloat(selectedPayment.amount).toLocaleString()}</span>
                        </div>
                        <div className="mb-3">
                          <strong className="text-muted d-block">Transaction Date:</strong>
                          <span className="text-dark">{new Date(selectedPayment.transaction_date).toLocaleDateString('en-IN')}</span>
                        </div>
                        <div className="mb-3">
                         
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
                
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Update Status <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        value={formData.status}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                        required
                        className="form-select-lg"
                      >
                        <option value="">Select</option>
                        <option value="PENDING">PENDING</option>
                        <option value="NOTFOUND">NOTFOUND</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Select the current status of this UPI payment
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            ) : (
              /* Create Mode - All Fields */
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>UTR Number *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.utr}
                      onChange={(e) => handleInputChange('utr', e.target.value)}
                      placeholder="Enter UTR number"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>VPA *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.vpa}
                      onChange={(e) => handleInputChange('vpa', e.target.value)}
                      placeholder="Enter VPA (e.g., 9876543210@upi)"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Order ID *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.orderId}
                      onChange={(e) => handleInputChange('orderId', e.target.value)}
                      placeholder="Enter Order ID"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Amount *</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>₹</InputGroup.Text>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => handleInputChange('amount', e.target.value)}
                        placeholder="Enter amount"
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={12} className="mb-3">
                  <Form.Group>
                    <Form.Label>Transaction Date *</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.transactionDate}
                      onChange={(e) => handleInputChange('transactionDate', e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
                setSelectedPayment(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              <i className="fas fa-save me-2"></i>
              {loading ? 'Saving...' : (selectedPayment ? 'Update Payment' : 'Create Payment')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default UPIPayments;